import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  formatDateTimeStamp,
  getFormattedDate,
  getFormattedDateTime,
  getFormattedTime,
  isLargeScreen,
  MAX_SHIFT_DURATION,
} from '../../../../../global.variable';
import { AppService } from '../../../../app.service';
import { AddCheckpointRouteComponent } from '../../../../shared/components/add-checkpoint-route/add-checkpoint-route.component';
import { ChangeScheduleComponent } from '../../../../shared/components/change-schedule/change-schedule.component';
import { ClockInOutComponent } from '../../../../shared/components/clock-in-out/clock-in-out.component';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ModalDialogueService } from '../../../../shared/components/modal-dialogue/modal-dialogue.service';
import { PrompterComponent } from '../../../../shared/components/prompter/prompter.component';
import { DataCheckService } from '../../../../shared/services/data-check.service';
import { gTDB } from '../../../../shared/services/db';
import { DeviceInfoService } from '../../../../shared/services/device-info.service';
import { LoadingSpinnerService } from '../../../../shared/services/loading-spinner.service';
import { NativeBgUploadService } from '../../../../shared/services/native-bg-upload.service';
import { SqliteDbService } from '../../../../shared/services/sqlite-db.service';
import { ToasterService } from '../../../../shared/services/toaster.service';
import { WebSocketService } from '../../../../shared/services/web-socket.service';
import { JobsService } from '../../jobs/jobs.service';
import { PagesService } from '../../pages.service';
import { UserPromptService } from '../../user-prompts/user-prompt.service';
import { RunSheetService } from '../runsheet.service';

@Component({
  selector: 'gtapp-runsheet-detail',
  templateUrl: './runsheet-detail.component.html',
  styleUrl: './runsheet-detail.component.scss',
  standalone: false,
})
export class RunSheetDetailComponent implements OnInit, OnDestroy {
  routeSubscription: Subscription = new Subscription();
  deviceInfoSubscription: Subscription = new Subscription();
  webSocketSubscription: Subscription = new Subscription();
  nativeUploadSubscription: Subscription = new Subscription();

  loggedInDeviceID: string = this.deviceInfoService.getUniqueDeviceID();
  largeView: Boolean = isLargeScreen;
  updateBtnPressed: boolean = false;

  runSheetId: string = '';
  runSheetDetailData: any;
  newRunSheetName: string = '';
  isCurrentORFutureSchedule: boolean = false;
  startsAfterToday: boolean = false;

  completedCheckListCount: number = 0;

  isAdmin: boolean = this.dataCheckService.isUserAdmin();
  isDispatchUser: boolean = this.dataCheckService.isDispatchUser({
    permission: 'job_type',
    action: 'edit',
    type_identity: 6,
  });

  schedulerForm: UntypedFormGroup = new UntypedFormGroup({
    end_time: new FormControl(null),
    start_time: new FormControl(null),
    start_day: new FormControl(new Date()),
    end_day: new FormControl(null, Validators.required),
    repeat_times: new UntypedFormControl(null),
    repeat_type: new UntypedFormControl(null, Validators.required),
    repeat_details: new UntypedFormControl([]),
    repeat_interval: new UntypedFormControl(1, [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
    ]),
    position: new UntypedFormControl(null, []),
    week_day: new UntypedFormControl(null, []),
    month_repeat_case: new UntypedFormControl(null, []),
  });
  endDateTime: any;
  startDateTime: any;

  originalDateTimeShift: any = {};

  disableShiftUpdate: boolean = false;
  shiftTimeExceeded: boolean = false;

  validStartDate: any;
  hasJobStarted: boolean = false;

  syncInProgress: boolean = false;
  refreshData: boolean = false;
  offlineMode: boolean = false;
  messageChannel = new MessageChannel();

  assigneeList: any = [];
  selectedAssignee: any = {};

  dialogRef: any;

  runSheetHistoryLog: any = [];
  completedCheckListData: any = {};
  runSheetHistoryTableDetails: any = [
    {
      header: 'Date / Update By',
      name: 'user_name',

      sortKey: 'updated_datetime',
      dateRangeKey: 'updated_datetime',
      sortOrder: 'desc',

      prefixNestedValue: (row: any) => {
        return (row?.updated_datetime ?? row?.updated_at)
          ? `${formatDateTimeStamp(
              row?.updated_datetime ?? row?.updated_at,
              'd MMM y HH:mm',
              'en_US',
            )} \n`
          : '';
      },

      tableColumnStyle: {
        'white-space': 'pre-line',
      },
    },

    {
      header: 'Update',
      name: 'action',
      sortKey: 'action',
      searchKey: 'action',
      sortOrder: 'desc',
    },
  ];

  otherParams: any = {
    tableRowStyle: (rowData: any) => {
      if (rowData?.is_deleted) {
        return {
          opacity: '0.6',
        };
      }
      return {};
    },
  };
  selectedEvent: any;
  selectedEventId: string = ''; // for re opening after any update to the event

  checkListData: any = [{ tasks: [{}] }];
  siteList: any = [];
  initalsiteListData: any = [];
  selectedSiteData: any = {};

  selectedCheckListTask: any;

  subscriberUserId: any;

  itemsNotInServer: any = [];
  errorItemsIdsInQueue = new Set();
  loggedInUpdatesSet = new Set<string>();

  taskEndDate: any;

  strSearchSite: string = '';
  filteredSiteList: any = [];
  openIndex: any = 0;
  activeTab: string = 'pending';

  subscriberSettingsInfo: any = {};
  runSheetJobType: any = {};
  selectedCheckList: any;

  showTasksMenu: boolean = false;
  runSheetEndDateTime: any;
  runSheetStartDateTime: any;

  shiftClockInOutParms = {
    requiresClockInOut: false,
    hasUserClockedIn: false,
    hasUserClockedOut: false,
    runsheetId: '',
    shiftStartTime: new Date(),
    shiftEndTime: new Date(),
  };

  cachedJobIds: any = [];

  userPromptsSearchResults: any = [];
  userPromptsSearchResultsCopy: any = [];
  userPromptTotalCount: number = 0;

  isNativeApp = Capacitor.isNativePlatform();

  fetchingDataFromServer = false;

  constructor(
    private route: ActivatedRoute,
    private runSheetService: RunSheetService,
    private spinnerService: LoadingSpinnerService,
    private dialogService: ModalDialogueService,
    private router: Router,
    private toasterService: ToasterService,
    private viewContainerRef: ViewContainerRef,
    private dataCheckService: DataCheckService,
    private deviceInfoService: DeviceInfoService,
    private jobService: JobsService,
    private appService: AppService,
    private pageService: PagesService,
    private webSocketService: WebSocketService,
    private userPromptService: UserPromptService,
    private nativeUploadService: NativeBgUploadService,
    private sqliteDb: SqliteDbService,
  ) {
    const rKey = this.route.snapshot.paramMap.get('rKey');
    if (rKey) {
      this.runSheetId = rKey;
      this.getRunSheetDetailData();
    }
  }
  ngOnDestroy(): void {
    if (this.webSocketSubscription) this.webSocketSubscription?.unsubscribe();
    if (this.nativeUploadSubscription)
      this.nativeUploadSubscription?.unsubscribe();

    if (this.deviceInfoSubscription) this.deviceInfoSubscription?.unsubscribe();
    this.messageChannel.port1.onmessage = null;
  }
  ngOnInit(): void {
    this.getJobTypes();
    const userData = this.appService.getUserData();
    this.subscriberUserId = userData?.subscriber?.subscriber_user_id;

    this.deviceInfoSubscription =
      this.deviceInfoService.deviceInfoSubject.subscribe((value: any) => {
        if ('appStatus' in value) {
          if (this.offlineMode && value?.appStatus) {
            this.refreshData = true;
          }
          this.offlineMode = !value?.appStatus;
        }
        if ('syncComplete' in value) {
          this.syncInProgress = false;
        }
      });
    this.getDataFromSW();
    this.getDeviceID();
    this.startWebSocketCommunication();
    this.checkJobsPresentInIndexedDb();
    this.getDataFromNativeUploads();
  }
  checkJobsPresentInIndexedDb() {
    this.cachedJobIds = JSON.parse(
      localStorage.getItem(btoa('indexedDbJobIdsToKeep')) || '[]',
    );
  }
  getDataFromNativeUploads() {
    this.nativeUploadSubscription =
      this.nativeUploadService.apiResponseSubject.subscribe((value: any) => {
        if (
          this.runSheetDetailData?.id &&
          value?.data?.id == this.runSheetDetailData?.id
        ) {
          setTimeout(() => {
            this.runSheetDetailData = value?.data;
            this.formatRunSheetData();
          }, 500);
        }
      });
  }

  getDataFromSW() {
    navigator?.serviceWorker?.controller?.postMessage(
      {
        type: 'INIT_RUN_SHEET_DETAIL_PORT',
      },
      [this.messageChannel.port2],
    );

    this.messageChannel.port1.onmessage = (event) => {
      if (event?.data?.data?.id) {
        this.runSheetDetailData = event?.data?.data;
        this.formatRunSheetData();
      }
    };
  }

  getDeviceID() {
    if (!this.loggedInDeviceID) {
      this.loggedInDeviceID = this.deviceInfoService.getUniqueDeviceID();
    }
  }
  startWebSocketCommunication() {
    this.webSocketSubscription =
      this.webSocketService.webSocketSubject.subscribe((event: any) => {
        if (
          this.loggedInDeviceID &&
          this.loggedInDeviceID !== event?.request_user?.logged_in_device_id
        ) {
          if (event?.data?.id === this.runSheetDetailData?.id) {
            if (event?.event === 'wsn_individual_runsheet_update') {
              // making sure the device is not the same as the change orginated from
              this.runSheetDetailData = event?.data;
              this.formatRunSheetData();
            }
            if (event?.event === 'wsn_delete_runsheet') {
              this.router.navigate(['/runsheet']);
            }
          }
          if (
            event?.data?.run_sheet?.id === this.runSheetDetailData?.id &&
            event?.event === 'individual_job_update'
          ) {
            // the user marked the task as completed from the job page so just update the completedCheckListData dict
            const completedCheckListTasks =
              event?.data?.check_list_tasks?.filter(
                (task: any) => task?.task_completed_info?.updated_datetime,
              );

            completedCheckListTasks?.forEach((item: any) => {
              this.completedCheckListData[item?.id] = item?.task_completed_info;
            });
          }
        }
      });
  }
  async getJobDetailsFromClient(id: string) {
    let result: any;
    // if (this.isNativeApp) {
    //   try {
    //     result = await this.sqliteDb.getCachedItemRow('job', id);
    //   } catch (error) {
    //     throw error;
    //   }
    // } else {
    result = await gTDB.cachedData
      .where('shortPath')
      .equals(`runsheet_${id}`)
      .first();
    // }

    this.runSheetDetailData = result?.runSheetDetailData;
    this.formatRunSheetData();
    this.fetchingDataFromServer = true;
  }
  getRunSheetDetailFromServer() {
    this.runSheetService
      .getRunSheetDetailForDesktop(this.runSheetId)
      .subscribe((response: any) => {
        if (response?.status === 'success') {
          this.runSheetDetailData = response?.data;
          this.formatRunSheetData();
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }

  getRunSheetDetailData() {
    setTimeout(() => {
      this.pageService.miscSubjectParam.next({
        isOnEvent: true,
      });
    }, 100);
    this.spinnerService.show('Fetching Data');
    this.getJobDetailsFromClient(this.runSheetId);
    this.getRunSheetDetailFromServer();
  }

  formatRunSheetData() {
    this.updateBtnPressed = false;
    this.formatRunSheetHistory(this.runSheetDetailData?.history_data);

    if (
      !this.runSheetDetailData?.check_list_data?.pending_tasks?.length &&
      this.runSheetDetailData?.check_list_data?.completed_tasks?.length
    )
      this.activeTab = 'completed';

    if (
      this.runSheetDetailData?.check_list_data?.pending_tasks?.length &&
      !this.runSheetDetailData?.check_list_data?.completed_tasks?.length
    )
      this.activeTab = 'pending';

    this.openIndex =
      this.openIndex >
      (this.activeTab == 'pending'
        ? this.runSheetDetailData?.check_list_data?.pending_tasks?.length - 1
        : this.runSheetDetailData?.check_list_data?.completed_tasks?.length - 1)
        ? 0
        : this.openIndex;

    this.isPastScheduleCheck();
    this.showPatrolDetails();
    this.fetchingDataFromServer = false;

    this.spinnerService.hide();
    this.updateJobDetailsToClient();
  }
  async updateJobDetailsToClient() {
    if (!this.runSheetDetailData?.id) return;
    const shortPath = `runsheet_${this.runSheetDetailData?.id}`;
    const jobData = {
      runSheetDetailData: this.runSheetDetailData,
      shiftClockInOutParms: this.shiftClockInOutParms,
      shortPath: shortPath,
      updatedAt: new Date().getTime(),
    };
    await gTDB.cachedData.put(jobData, shortPath);
  }
  formatRunSheetHistory(historyData: any) {
    this.runSheetHistoryLog = JSON.parse(JSON.stringify(historyData));

    if (this.selectedEventId) {
      // in case of delete/restore/edit comments reopen the selected event after the update has been made.
      const updatedEvent = this.runSheetHistoryLog?.find(
        (update: any) => update?.id === this.selectedEventId,
      );
      if (updatedEvent?.id) this.onTableRowClick(updatedEvent);
    }

    this.formatCachedList();
  }
  showOfflineMessage() {
    this.toasterService.setMessage({
      successMessage: '',
      errorMessage: 'Feature not available while offline',
    });
  }

  isPastScheduleCheck() {
    const endDateTime = new Date(
      (this.runSheetDetailData?.schedule_event_date ||
        this.runSheetDetailData.roster_scheduler.end_day) +
        ' ' +
        this.runSheetDetailData.roster_scheduler.end_time,
    );
    if (
      this.runSheetDetailData.roster_scheduler?.start_time >
        this.runSheetDetailData.roster_scheduler?.end_time &&
      this.runSheetDetailData?.roster_scheduler?.repeat_type != 'once'
    ) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    this.runSheetEndDateTime = new Date(endDateTime);

    endDateTime.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.isCurrentORFutureSchedule = endDateTime.getTime() >= today.getTime();

    const startDatetime = new Date(
      (this.runSheetDetailData?.schedule_event_date ||
        this.runSheetDetailData.roster_scheduler.start_day) +
        ' ' +
        this.runSheetDetailData.roster_scheduler.start_time,
    );

    this.runSheetStartDateTime = new Date(startDatetime);

    if (this.runSheetDetailData?.assignee?.id === this.subscriberUserId) {
      let requiresClockInOut = this.dataCheckService.requireClockInOut();
      requiresClockInOut = this.runSheetDetailData
        ?.user_current_clock_status_for_shift
        ? requiresClockInOut
        : false;
      if (requiresClockInOut) {
        this.shiftClockInOutParms.hasUserClockedIn =
          !!this.runSheetDetailData?.user_current_clock_status_for_shift?.[0];

        this.shiftClockInOutParms.hasUserClockedOut =
          !!this.runSheetDetailData?.user_current_clock_status_for_shift?.[1];
      }

      this.shiftClockInOutParms = {
        ...this.shiftClockInOutParms,
        requiresClockInOut: requiresClockInOut,
        runsheetId: this.runSheetDetailData?.id,
        shiftStartTime: this.runSheetStartDateTime,
        shiftEndTime: this.runSheetEndDateTime,
      };
    }

    this.showTasksMenu =
      this.isAdmin ||
      (this.shiftClockInOutParms?.hasUserClockedIn &&
        !this.shiftClockInOutParms?.hasUserClockedOut) ||
      !this.shiftClockInOutParms?.requiresClockInOut;

    startDatetime.setHours(0, 0, 0, 0);

    const tommorrow = new Date(today);
    tommorrow.setDate(tommorrow.getDate() + 1);

    this.startsAfterToday = startDatetime.getTime() >= tommorrow.getTime();
    this.endDateTime =
      endDateTime.getTime() == startDatetime.getTime()
        ? null
        : new Date(endDateTime);
  }
  preFormatScheduleForApi(schedule: any) {
    let formattedSchedule: any = {};
    if (schedule?.start_time) {
      formattedSchedule.start_time = schedule?.start_time;
    }
    if (schedule?.end_time) {
      formattedSchedule.end_time = schedule?.end_time;
    }
    if (schedule?.start_datetime) {
      formattedSchedule.start_day = getFormattedDate(schedule?.start_datetime);
      formattedSchedule.start_time = getFormattedTime(schedule?.start_datetime);
    }
    if (schedule?.end_datetime) {
      formattedSchedule.end_day = getFormattedDate(schedule?.end_datetime);
      formattedSchedule.end_time = getFormattedTime(schedule?.end_datetime);
    }

    formattedSchedule.reminder = true;

    return formattedSchedule;
  }

  updateRunSheet(updateType: string, reqBody?: any, params?: any) {
    this.spinnerService.show();
    this.updateBtnPressed = true;
    let updateData: any = { run_sheet_id: this.runSheetId, ...(reqBody || {}) };
    let reqParams: any = { return_data: 1, ...(params || {}) };

    switch (updateType) {
      case 'assignee':
        updateData.subscriber_user_id = this.selectedAssignee?.id;
        reqParams.update_assignee = 1;
        break;

      case 'name':
        updateData.name = this.newRunSheetName;
        reqParams.update_name = 1;
        break;
      case 'schedule':
        reqParams.update_run_sheet_shift = 1;
        updateData = {
          ...updateData,
          ...this.schedulerForm.value,
          time_offset: new Date().getTimezoneOffset(),
        };
        break;
      case 'delete_task':
        reqParams.delete_task = 1;
        break;
      case 'checkList_tasks':
        reqParams.update_checklist_tasks = 1;
        let check_list_data_tasks: any = [];
        let indexBlock = this.runSheetDetailData?.final_order_number || 0;
        this.selectedCheckList?.tasks?.forEach((task: any) => {
          if (task?.note?.trim()) {
            let order = task?.order;
            if (!order) {
              indexBlock += 1;
              order = indexBlock;
            }

            check_list_data_tasks.push({
              id: task?.id,
              notes: task?.note?.trim(),
              user_prompt_id: task?.user_prompt_id,
              schedule: this.preFormatScheduleForApi(task?.schedule),
              order: order,
              site_id: this.selectedCheckList?.site_data?.id,
            });
          }
        });
        if (!check_list_data_tasks?.length) {
          this.spinnerService.hide();
          return;
        }
        updateData.check_list_data = {
          ...this.selectedCheckList,

          tasks: check_list_data_tasks,
        };

        break;
      case 'checkListTask':
        reqParams.update_checklist_task = 1;
        break;
      case 'checkListTaskReminder':
        reqParams.update_checklist_task_reminder = 1;
        if (updateData?.schedule && Object.keys(updateData?.schedule)?.length) {
          updateData.schedule = this.preFormatScheduleForApi(
            updateData?.schedule,
          );
        }
        break;
      case 'checkList':
        reqParams.add_checklist = 1;
        let checkListData: any = [];
        let index = this.runSheetDetailData?.final_order_number || 0;
        this.checkListData?.forEach((checkList: any) => {
          let updatedCheckList: any = [];
          const siteId = checkList?.site?.id || 0;
          if (checkList?.tasks?.length) {
            checkList?.tasks?.forEach((task: any) => {
              if (task?.note?.trim()) {
                index += 1;
                updatedCheckList.push({
                  notes: task?.note?.trim(),
                  user_prompt_id: task?.user_prompt_id,
                  schedule: this.preFormatScheduleForApi(task?.schedule),
                  order: index,
                });
              }
            });
          }

          if (checkList?.enable_reporting && !updatedCheckList?.length) {
            index += 1;

            updatedCheckList.push({
              notes: null,
              order: index,
            });
          }

          if (updatedCheckList?.length) {
            const checkListDataDict: any = {
              tasks: updatedCheckList,
              site_id: siteId,
              check_list_schedule_uuid: uuidv4(),
            };
            if (
              siteId &&
              checkList?.enable_reporting &&
              this.runSheetJobType?.id
            ) {
              checkListDataDict['create_job'] = {
                company_id: checkList?.site?.company?.id,
                job_type_id: this.runSheetJobType?.id,
              };
              const checkpointIds = checkList?.selectedCheckpoints
                ?.map((item: any) => item?.id)
                ?.filter(Boolean);
              checkListDataDict['checkpoint_ids'] = checkpointIds || [];
            }

            checkListData.push(checkListDataDict);
          }
        });

        updateData.check_list_data = checkListData;
        break;
      case 'enable_reporting':
        reqParams.enable_reporting = 1;
        if (
          new Date(this.runSheetStartDateTime).getTime() < new Date().getTime()
        ) {
          //https://app.shortcut.com/sendem/story/11960/upgrading-historical-scheduled-patrol-runsheets
          reqParams.auto_complete = 1;
        }
        break;
      case 'delete_checklist':
        reqParams.delete_checklist = 1;
        break;

      case 'update_checkpoints':
        reqParams.update_checkpoints = 1;
    }

    this.runSheetService
      .updateRunSheet(this.runSheetId, updateData, reqParams)
      .then((response: any) => {
        if (response?.status === 'success') {
          this.runSheetDetailData = response?.data;
          this.dialogRef?.close();
          this.formatRunSheetData();

          this.toasterService.setMessage({
            successMessage: response?.message,
            errorMessage: '',
          });
        } else {
          this.spinnerService.hide();
          this.toasterService.setMessage({
            successMessage: '',
            errorMessage: response?.message,
          });
          this.updateBtnPressed = false;
        }
      });
  }
  openTemplate(template: any, updateItem: string, eventData?: any) {
    this.runSheetDetailData.current_shift_only = false;
    let data: any = {};
    if (updateItem === 'comment') {
      data = {};
    } else if (updateItem === 'editComment') {
      data = {
        newComment: eventData?.detail,
        previousComment: eventData?.detail,
        id: eventData?.id,
      };
    } else if (updateItem === 'assignee') {
      this.selectedAssignee = this.runSheetDetailData?.assignee;
      this.assigneeList = [];
      this.getAssignees();
    } else if (updateItem === 'name') {
      this.newRunSheetName = this.runSheetDetailData?.name;
    } else if (updateItem == 'checklist') {
      data = {
        schedulerInfo: {
          value: {
            ...this.runSheetDetailData?.roster_scheduler,
            start_datetime: this.runSheetDetailData?.start_datetime,
            end_datetime: this.runSheetDetailData?.end_datetime,
          },
        },
      };

      this.checkListData = [{ tasks: [{}] }];
    } else if (updateItem == 'checklist_tasks') {
      const upatedEventData = JSON.parse(JSON.stringify(eventData));
      upatedEventData.tasks =
        upatedEventData?.tasks?.filter((item: any) => !item?.id) || [];

      this.selectedCheckList = JSON.parse(JSON.stringify(upatedEventData));
      if (!('tasks' in this.selectedCheckList)) {
        this.selectedCheckList['tasks'] = [];
      }
      this.selectedCheckList['tasks'].push({});
      this.getUserPrompts({}, true);
    } else if (updateItem == 'delete_checklist') {
      const upatedEventData = JSON.parse(JSON.stringify(eventData));
      data = upatedEventData;
    } else if (updateItem == 'prompts') {
      data = JSON.parse(JSON.stringify(eventData));
    }
    this.dialogRef = this.dialogService.open(
      template,
      {
        data: data,
      },
      this.viewContainerRef,
    );
  }
  changeShiftDateTime(dateTimeType: number, event: any) {
    let startDateTimeValid = true;
    if (dateTimeType === 0) {
      this.schedulerForm.controls['start_day'].setValue(
        getFormattedDate(event.target.value),
      );
      const selectedDate = new Date(event.target.value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        this.schedulerForm.controls['start_day'].setErrors({
          invalidStartDate: true,
        });
        startDateTimeValid = false;
      } else {
        this.schedulerForm.controls['start_day'].setErrors(null);
      }
      this.schedulerForm.controls['start_time'].setValue(
        getFormattedTime(event.target.value),
      );
      const scheduleEndDateTime = new Date(
        this.schedulerForm.value.end_day +
          ' ' +
          this.schedulerForm.value.end_time,
      );

      if (
        scheduleEndDateTime.getTime() < new Date(event.target.value).getTime()
      ) {
        const newEndDate = new Date(event.target.value);
        newEndDate.setHours(newEndDate.getHours() + 8);

        this.schedulerForm.controls['end_day'].setValue(
          getFormattedDate(newEndDate),
        );
        this.schedulerForm.controls['end_time'].setValue(
          getFormattedTime(newEndDate),
        );
        this.endDateTime = getFormattedDateTime(newEndDate);
      }
    } else {
      this.schedulerForm.controls['end_day'].setValue(
        getFormattedDate(event.target.value),
      );

      if (
        new Date(getFormattedDate(event.target.value)) <
        new Date(this.schedulerForm.value.start_day)
      ) {
        this.schedulerForm.controls['end_day'].setErrors({
          invalidStartDate: true,
        });
        startDateTimeValid = false;
      } else {
        this.schedulerForm.controls['end_day'].setErrors(null);
      }
      this.schedulerForm.controls['end_time'].setValue(
        getFormattedTime(event.target.value),
      );
    }
    this.shiftTimeExceeded =
      new Date(this.endDateTime)?.getTime() -
        new Date(this.startDateTime)?.getTime() >
      MAX_SHIFT_DURATION * 60 * 60 * 1000;

    this.disableShiftUpdate =
      (new Date(this.originalDateTimeShift?.startDateTime)?.getTime() ==
        new Date(this.startDateTime)?.getTime() &&
        new Date(this.originalDateTimeShift?.endDateTime)?.getTime() ==
          new Date(this.endDateTime)?.getTime()) ||
      !startDateTimeValid ||
      new Date(this.startDateTime)?.getTime() ===
        new Date(this.endDateTime)?.getTime() ||
      this.shiftTimeExceeded;
  }

  clearAndFocusTextBox(elementId: string) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 100);
  }
  async uploadFormData(formData: any) {
    formData.append('run_sheet_id', this.runSheetDetailData?.id);
    const response: any = await this.appService.backUpAndUploadUpdate(
      `run_sheet/add_update`,
      formData,
      {},
      'POST',
    );

    this.formatRunSheetHistory(this.runSheetHistoryLog);
    if (this.offlineMode && response?.status == 'success') {
      this.toasterService.setMessage({
        successMessage: 'Action Queued',
        errorMessage: '',
      });
    }

    if (response?.data) {
      this.runSheetDetailData = response['data'];
      this.formatRunSheetData();
    }

    this.spinnerService.hide();
    return true;
  }
  async openPrompterComponent(promptInfo: any, taskId?: any) {
    const dialogRef = this.dialogService.open(PrompterComponent, {
      data: { promptData: promptInfo?.prompts },
    });
    const value = await firstValueFrom(dialogRef.afterClosed());
    if (!value?.length) return false;

    let fileData: FormData = new FormData();
    if (taskId) {
      this.spinnerService.show('Marking Complete - Please Wait');
      fileData.append('check_list_id', taskId);
    } else {
      this.spinnerService.show();
      // This will be the safety prompt check. Provide a diff event_action for it
      fileData.append('safety_prompt', '1');
    }
    fileData.append('user_prompt_id', promptInfo?.id);
    fileData.append(
      'prompt_data',
      JSON.stringify([
        {
          data: value,
          prompt_data: {
            id: promptInfo?.id,
            name: promptInfo?.note,
          },
        },
      ]),
    );
    fileData.append('mark_complete', '1');
    fileData.append('return_data', '1');
    fileData.append(
      'runSheetDetails',
      JSON.stringify({
        name: this.runSheetDetailData?.name,
      }),
    );
    return await this.uploadFormData(fileData);
  }
  async openCheckListMarkCompleteTemplate(template: any, task: any) {
    if (task?.prompt?.id && task?.prompt?.prompts?.length) {
      this.openPrompterComponent(task?.prompt, task?.id);
      return;
    }
    const dialogRef = this.dialogService.open(
      template,
      {
        data: task,
      },
      this.viewContainerRef,
    );
    const dResponse = await firstValueFrom(dialogRef?.afterClosed());
    if (dResponse?.id) {
      this.spinnerService.show('Marking Complete - Please Wait');
      let fileData: FormData = new FormData();

      if (dResponse?.newComment) {
        fileData.append('comment', dResponse?.newComment);
      }
      fileData.append('check_list_id', dResponse?.id);
      fileData.append('mark_complete', '1');
      fileData.append('return_data', '1');
      fileData.append(
        'runSheetDetails',
        JSON.stringify({
          name: this.runSheetDetailData?.name,
        }),
      );
      this.uploadFormData(fileData);
    }
  }
  onTableRowClick(event: any) {
    this.selectedEvent = event;
    this.selectedEventId = event?.id;
  }
  redirectToSite(siteId: any) {
    this.router.navigate(['/view-site', { sKey: String(siteId) }]);
  }
  deleteCheckListTask(taskData: any) {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Task?',
        message: `THIS ACTION CANNOT BE UNDONE!`,
        btn2Detail: { text: 'Delete', class: 'btn-danger' },
        messageStyle: { 'white-space': 'pre-line' },
      },
    });
    dialogRef.afterClosed().subscribe((value) => {
      if (value === true) {
        this.updateRunSheet('delete_task', { check_list_id: taskData?.id });
      }
    });
  }

  onInputTaskNote(event: any, currentTask: any, taskList?: any) {
    const textarea: any = event.target as HTMLTextAreaElement;
    const maxHeight = 150;

    if (currentTask) {
      currentTask.ngStyle = {
        height: Math.max(62, Math.min(textarea.scrollHeight, maxHeight)) + 'px',
        overflowY: textarea.scrollHeight > maxHeight ? 'auto' : 'hidden',
      };
    }
    if (taskList) this.normalizeTaskList(taskList);
  }
  normalizeTaskList(taskList: any) {
    const nonEmptyTasks = taskList.filter((task: any) => task?.note?.trim());

    const removedItem = taskList?.length - nonEmptyTasks?.length == 2;

    taskList.length = 0;
    taskList.push(...nonEmptyTasks);

    taskList.push({ note: '' });

    if (removedItem) {
      setTimeout(() => {
        const allTextareas = document.querySelectorAll('textarea');

        const lastTextarea = allTextareas[
          allTextareas.length - 1
        ] as HTMLTextAreaElement;

        lastTextarea?.focus();
      }, 0);
    }
  }
  filterEmptyTasks() {
    this.checkListData?.forEach((checkList: any) => {
      checkList.tasks = checkList?.tasks?.filter((task: any) =>
        task?.note?.trim(),
      );
    });
    this.checkListData.push({ tasks: [{}] });
  }
  duplicateTask(taskList: any, subIndex: number) {
    taskList?.splice(subIndex + 1, 0, { ...taskList[subIndex], id: null });
  }

  isSameDayCheck = (date: any) => {
    const dateObj = new Date(date);
    dateObj.setHours(0, 0, 0, 0);
    return dateObj.getTime();
  };
  formatTaskSchedule(task: any, editSchedule = false) {
    let taskSchedule: any = {
      invalid: {},
      showItems: {},
      showSchedule: true,
      editSchedule: editSchedule,
    };

    taskSchedule.reminder = task?.schedule?.reminder;
    taskSchedule.start_datetime = getFormattedDateTime(
      task?.schedule?.start_datetime || this.runSheetDetailData?.start_datetime,
    );
    taskSchedule.end_datetime = getFormattedDateTime(
      task?.schedule?.end_datetime || this.runSheetDetailData?.end_datetime,
    );
    taskSchedule.start_time = getFormattedTime(taskSchedule.start_datetime);
    taskSchedule.end_time = getFormattedTime(taskSchedule.end_datetime);

    taskSchedule.minDateTime = getFormattedDateTime(
      Math.max(
        new Date().getTime(),
        Math.max(new Date(this.runSheetDetailData?.start_datetime).getTime()),
      ),
    );

    taskSchedule.maxDateTime = getFormattedDateTime(
      this.runSheetDetailData?.end_datetime,
    );

    taskSchedule.showItems.startEndDateTime =
      this.isSameDayCheck(taskSchedule.minDateTime) !==
      this.isSameDayCheck(taskSchedule.maxDateTime);

    taskSchedule.showItems.startEndTime =
      this.isSameDayCheck(taskSchedule.minDateTime) ===
      this.isSameDayCheck(taskSchedule.maxDateTime);
    return taskSchedule;
  }

  editCheckListTextTemplate(
    template: any,
    task: any,
    checkList: any,
    editSchedule = false,
  ) {
    const dataObj = {
      ...checkList,
      task: task,
      schedule: this.formatTaskSchedule(task, editSchedule),
    };
    this.selectedCheckListTask = JSON.parse(JSON.stringify(dataObj));
    this.dialogRef = this.dialogService.open(
      template,
      {
        data: dataObj,
      },
      this.viewContainerRef,
    );
  }

  async getAllIndexedDbItems() {
    try {
      const data = await gTDB.gtSavedData.toArray();

      return data;
    } catch (error) {
      return [];
    }
  }
  formatCachedData(item: any) {
    let deviceInfo = JSON.parse(item?.device_info);

    let eventDict: any = {};
    if (item?.pathName?.includes('api/run_sheet/add_update/')) {
      if (item?.image_source === 'live') {
        eventDict = { event_action: 8, action: 'Uploaded Image' };
      } else if (item?.image_source === 'device') {
        eventDict = { event_action: 19, action: 'File Uploaded' };
      } else if (item?.mark_complete == '1') {
        eventDict = { event_action: 9, action: 'Task Completed' };
      } else {
        eventDict = { event_action: 10, action: 'Added Comment' };
      }
    }

    return {
      run_sheet_checklist_id: item?.check_list_id,
      cachedData: true,
      detail: item?.comment || null,
      gps: deviceInfo?.gps,
      updated_at: item?.updated_at * 1000,
      recorded_at: item?.updated_at * 1000,
      updated_datetime: item?.updated_at * 1000,
      user_name: 'You',
      index_db_id: item?.indexDBId,
      file: item?.file || null,
      subs_user_id: this.subscriberUserId,
      ...eventDict,
    };
  }
  formatClientSideCachedItem(cachedItem: any, historyMap: any) {
    if (!cachedItem || cachedItem?.run_sheet_id !== this.runSheetDetailData?.id)
      return;

    const cachedItemTimestamp = (cachedItem?.updated_at || 0) * 1000;

    //  Existing History Item Updates
    if (cachedItem?.id) {
      if (
        cachedItem?.edit_comment ||
        cachedItem?.delete_update ||
        cachedItem?.restore_update
      ) {
        const update = historyMap.get(Number(cachedItem?.id));

        if (update) {
          update.cachedData = true;
        }
      }
      return;
    }

    // Remaining Data List Additions
    const lookupKey = `${cachedItem?.indexDBId}|${cachedItemTimestamp}`;

    if (!this.loggedInUpdatesSet.has(lookupKey)) {
      this.loggedInUpdatesSet.add(lookupKey);

      const data = this.formatCachedData(cachedItem);

      this.runSheetHistoryLog.push(data);
    }
  }
  async formatCachedList() {
    const apiResponseDict: any =
      (await gTDB.userData.get('apiResponseDict')) || {};

    this.itemsNotInServer = [];
    this.errorItemsIdsInQueue.clear();

    const itemsNotInServerIds = new Set();

    const loggedInUsersUpdates = this.runSheetHistoryLog?.filter(
      (item: any) =>
        item?.subs_user_id == this.subscriberUserId &&
        [10, 9].includes(item?.event_action),
    );
    let nativeDbDataList: any = [];
    if (this.isNativeApp) {
      try {
        nativeDbDataList = await this.sqliteDb.getSavedUpdateRow(
          'runsheet',
          this.runSheetDetailData?.id,
        );
      } catch (error) {
        throw error;
      }
    }
    const indexedDbListData: any = await this.getAllIndexedDbItems();

    const historyMap = new Map<any, any>();
    this.runSheetHistoryLog?.forEach((update: any) => {
      if (update?.id) historyMap.set(update.id, update);
    });

    loggedInUsersUpdates?.forEach((item: any) => {
      if (item?.index_db_id && item?.recorded_at) {
        const timestamp = new Date(item.recorded_at).getTime();

        this.loggedInUpdatesSet.add(`${item.index_db_id}|${timestamp}`);
      }
    });

    indexedDbListData.forEach((cachedItem: any) => {
      this.formatClientSideCachedItem(cachedItem, historyMap);

      const failedItem =
        apiResponseDict?.[cachedItem?.indexDBId]?.errorMessages?.length;
      if (failedItem) {
        // had an error

        cachedItem.errorMessages =
          apiResponseDict?.[cachedItem?.indexDBId]?.errorMessages;
        if (!itemsNotInServerIds.has(cachedItem?.indexDBId)) {
          itemsNotInServerIds.add(cachedItem?.indexDBId);
          this.itemsNotInServer.push(cachedItem);
        }
        this.errorItemsIdsInQueue.add(cachedItem?.indexDBId);
      }
      const notInServerYet = cachedItem?.cachedData || !cachedItem?.id;
      if (notInServerYet && !itemsNotInServerIds.has(cachedItem?.indexDBId)) {
        itemsNotInServerIds.add(cachedItem?.indexDBId);
        this.itemsNotInServer.push(cachedItem);
      }
    });

    for (const data of nativeDbDataList) {
      const requestData = JSON.parse(data?.data) || {};
      const cachedItem = requestData?.body || {};
      cachedItem.indexDBId = data?.id;
      cachedItem.updated_at = new Date(data?.updatedAt).getTime() / 1000;
      cachedItem.run_sheet_id = data?.eventId;
      cachedItem.isInNativeDb = true;
      cachedItem.pathName =
        this.pageService.getPathNameForSavedUpdatesInNativeDb(data?.type);
      if (requestData?.uploadedFilePath) {
        cachedItem.file = await this.appService.getDisplayUrlForFileInDevice(
          requestData.uploadedFilePath,
        );
      }
      this.formatClientSideCachedItem(cachedItem, historyMap);
      if (data?.status == 4 && data?.retryResponse) {
        // had an error
        const errorMessages = JSON.parse(data?.retryResponse);
        const lastError = errorMessages[errorMessages?.length - 1].split(':');
        if (lastError?.[1]) {
          cachedItem.errorMessages = [lastError?.[1]];
          if (!itemsNotInServerIds.has(cachedItem?.indexDBId)) {
            itemsNotInServerIds.add(cachedItem?.indexDBId);
            this.itemsNotInServer.push(cachedItem);
          }
          this.errorItemsIdsInQueue.add(cachedItem?.indexDBId);
        }
      }
      const notInServerYet = cachedItem?.cachedData || !cachedItem?.id;
      if (notInServerYet && !itemsNotInServerIds.has(cachedItem?.indexDBId)) {
        itemsNotInServerIds.add(cachedItem?.indexDBId);
        this.itemsNotInServer.push(cachedItem);
      }
    }
    this.runSheetHistoryLog = this.runSheetHistoryLog?.sort(
      (a: any, b: any) =>
        new Date(a?.updated_at).getTime() - new Date(b?.updated_at).getTime(),
    );

    this.runSheetHistoryLog?.forEach((item: any) => {
      if (item?.run_sheet_checklist_id && item?.event_action == 9) {
        this.completedCheckListData[item?.run_sheet_checklist_id] = item;
      }
    });
    this.completedCheckListCount = Object.keys(
      this.completedCheckListData,
    )?.length;

    this.appService.removeToolTips();

    this.appService.initializeBootstrapTooltips();
  }
  deleteItemFromIndexDb(pkId: number) {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove this update?',
        message: 'THIS ACTION CANNOT BE UNDONE!',
        btn2Detail: { text: 'Delete', class: 'btn-danger' },
      },
    });
    dialogRef.afterClosed().subscribe((dialogResponse) => {
      if (dialogResponse === true) {
        gTDB.gtSavedData
          .where({
            indexDBId: pkId,
          })
          .delete();
        setTimeout(() => {
          this.fetchRunSheetHistory();
        }, 50);
      }
    });
  }
  fetchRunSheetHistory() {
    this.runSheetService
      .getRunSheetDetailForDesktop(this.runSheetId, { get_history: 1 })
      .subscribe((response: any) => {
        if (response?.status === 'success') {
          this.formatRunSheetHistory(response?.data);
        } else {
          this.toasterService.setMessage({
            errorMessage: response?.message,
            successMessage: '',
          });
        }
      });
  }
  async retryUpdate(indexDbId: any) {
    const apiResponseDict: any =
      (await gTDB.userData.get('apiResponseDict')) || {};
    delete apiResponseDict[indexDbId];
    await gTDB.userData.put(apiResponseDict, 'apiResponseDict');

    this.syncData();
    setTimeout(() => {
      this.fetchRunSheetHistory();
    }, 50);

    setTimeout(() => {
      this.formatCachedList();
    }, 500);
    setTimeout(() => {
      this.appService.removeToolTips();
    }, 2000);
  }
  syncData() {
    this.pageService.miscSubjectParam.next({ syncData: true });
    this.syncInProgress = true;
  }
  retrySendingUpdate() {
    if (this.offlineMode) {
      this.showOfflineMessage();
    } else {
      this.syncData();
      this.fetchRunSheetHistory();
    }
    setTimeout(() => {
      this.appService.removeToolTips();
    }, 2000);
  }

  deleteRestoreUserUpdate(event: any) {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: event?.type === 'delete' ? 'Delete Event' : 'Restore Event',
        message:
          event?.type === 'delete'
            ? `Are you sure you want to delete this event?`
            : 'Are you sure you want to restore this event?',
      },
    });
    dialogRef.afterClosed().subscribe((value: any) => {
      if (value === true) {
        this.spinnerService.show('Please Wait');
        let fileData: FormData = new FormData();
        fileData.append('id', event?.event?.id);
        fileData.append(
          event?.type === 'delete' ? 'delete_update' : 'restore_update',
          '1',
        );

        fileData.append('return_data', '1');
        fileData.append(
          'runSheetDetails',
          JSON.stringify({
            name: this.runSheetDetailData?.name,
          }),
        );
        this.uploadFormData(fileData);
      }
    });
  }
  changeStartEndDateTime(
    event: any,
    schedule: any,
    field: string,
    invalidField: string,
  ) {
    const isTimeField = ['start_time', 'end_time'].includes(field);
    const isDateTimeField = ['start_datetime', 'end_datetime'].includes(field);

    const formatDate = (date: Date) =>
      isTimeField ? getFormattedTime(date) : getFormattedDateTime(date);

    const validateDate = (
      selectedDate: Date,
      minDateTime: Date,
      maxDateTime: Date,
    ) => {
      const isValid =
        selectedDate.getTime() >= minDateTime.getTime() &&
        selectedDate.getTime() <= maxDateTime.getTime();

      return {
        valid: isValid,
        formatted: formatDate(selectedDate),
      };
    };
    const roundDateTimeToQuarterHour = (date: Date): Date => {
      const msPerQuarter = 15 * 60 * 1000;

      const rounded = Math.round(date.getTime() / msPerQuarter) * msPerQuarter;
      return new Date(rounded);
    };

    const getSelectedDate = (minDateTime: Date, maxDateTime: Date) => {
      let selectedDate = new Date();

      if (isDateTimeField) {
        if (event?.target?.value) {
          selectedDate = new Date(event.target.value);
        }
      } else {
        // case where one time shift falls on the same day no need to show them day field just take start_datetime
        selectedDate =
          schedule?.start_datetime &&
          this.schedulerForm?.value?.repeat_type === 'once'
            ? new Date(schedule.start_datetime)
            : field === 'start_time'
              ? new Date(minDateTime)
              : new Date(maxDateTime);

        const [hours, minutes] = event.target.value?.split(':') || ['0', '0'];
        selectedDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      }

      return roundDateTimeToQuarterHour(selectedDate);
    };

    const getMinMaxDateTime = () => {
      let minDateTime = new Date(schedule?.minDateTime);
      let maxDateTime = new Date(schedule?.maxDateTime);

      return { minDateTime, maxDateTime };
    };

    const { minDateTime, maxDateTime } = getMinMaxDateTime();

    const selectedDate = getSelectedDate(minDateTime, maxDateTime);
    const { valid, formatted } = validateDate(
      selectedDate,
      minDateTime,
      maxDateTime,
    );

    const updateDateTimeField = (
      dateKey: 'start_datetime' | 'end_datetime',
    ) => {
      const baseDate = schedule?.[dateKey]
        ? new Date(schedule[dateKey])
        : new Date();

      const [hours, minutes] = formatted?.split(':') || ['0', '0'];
      baseDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      schedule[dateKey] = getFormattedDateTime(baseDate);
    };

    schedule[field] = formatted;

    // special case to update start_datetime and end_datetime fields in case when there is no day selection
    if (field === 'start_time') {
      updateDateTimeField('start_datetime');
    }

    if (field === 'end_time') {
      updateDateTimeField('end_datetime');
    }

    if (
      new Date(schedule?.start_datetime).getTime() >
      new Date(schedule?.end_datetime).getTime()
    ) {
      // do a check to see if start datetime > end datetime if so make end datetime = start datetime
      schedule.end_datetime = schedule?.start_datetime;
      schedule.end_time = getFormattedTime(new Date(schedule?.end_datetime));
    }
    schedule.invalid[invalidField] = !valid;
  }
  openTaskConfigTemplate(task: any, template: any) {
    const dialogRef = this.dialogService.open(
      template,
      {
        data: { task, schedule: this.formatTaskSchedule(task) },
      },
      this.viewContainerRef,
    );
    dialogRef?.afterClosed().subscribe((res: any) => {
      if (res) {
        const newSchedule = JSON.parse(JSON.stringify(res?.schedule));
        task.schedule = { reminder: true };
        if (newSchedule?.reminder) task.schedule.reminder = true;
        task.schedule.start_time = newSchedule?.start_time;
        task.schedule.end_time = newSchedule?.end_time;
        task.schedule.start_datetime = newSchedule?.start_datetime;
        task.schedule.end_datetime = newSchedule?.end_datetime;
      }
    });
  }

  filterDataAndTasks(data: any, searchTerm: string) {
    const lowerTerm = searchTerm.toLowerCase();

    return data
      ?.map((item: any) => {
        const nameMatches = item?.site_data?.company_name
          ?.toLowerCase()
          .includes(lowerTerm);
        if (nameMatches) {
          return item;
        }

        const filteredTasks =
          item?.tasks?.filter((task: any) =>
            task?.note?.toLowerCase().includes(lowerTerm),
          ) || [];

        if (filteredTasks.length > 0) {
          return {
            ...item,
            tasks: filteredTasks,
          };
        }

        return null;
      })
      .filter((item: any) => item !== null && item?.tasks?.length > 0);
  }
  getfilteredSiteListData(data: any, event?: any) {
    this.strSearchSite = event?.target?.value?.toLowerCase();
    this.filteredSiteList = [];

    data?.forEach((checkList: any, index: number) => {
      if (checkList?.site_data?.id) {
        let siteData: any = checkList?.site_data;
        siteData['index'] = index;
        const pendingTasks = checkList?.tasks?.filter(
          (task: any) => !(task?.id in this.completedCheckListData),
        );

        siteData['taskCount'] = pendingTasks?.length;
        if (this.strSearchSite) {
          if (
            checkList?.site_data?.company_name
              ?.toLowerCase()
              .includes(this.strSearchSite)
          ) {
            this.filteredSiteList.push(siteData);
          }
        } else {
          this.filteredSiteList.push(siteData);
        }
      }
    });
    this.filteredSiteList = this.filteredSiteList?.splice(0, 3);
  }
  scrollToSelectedSite(event: any) {
    const lastItemElement: any = document.getElementById(event?.id);
    if (lastItemElement) {
      this.openIndex = event?.index || 0;
      this.showPatrolDetails();
      lastItemElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    }
  }

  setActiveTab(tab: 'pending' | 'completed') {
    this.activeTab = tab;
    this.openIndex = 0;
    this.showPatrolDetails();
  }
  redirectToJob(jobData: any) {
    if (this.offlineMode && !this.cachedJobIds?.includes(String(jobData?.id))) {
      this.toasterService.setMessage({
        successMessage: '',
        errorMessage:
          'Unable to fetch details from server. Please try again later',
      });
      return;
    }
    this.router.navigate([`/job-detail/${jobData?.job_key}`], {
      queryParams: { jId: jobData?.id },
    });
  }
  getJobTypes() {
    this.jobService.getJobParams().subscribe((response: any) => {
      if (response['status'] == 'success') {
        this.runSheetJobType = response?.data?.find(
          (jType: any) => jType?.type_identity === 6,
        );
      }
    });
  }
  requireSafetyCheck() {
    if (this.shiftClockInOutParms?.hasUserClockedIn) return [];

    const completedPrompts = new Set(
      this.runSheetDetailData?.history_data?.flatMap((item: any) =>
        item?.prompts?.map((prompt: any) => prompt?.prompt_data?.id),
      ),
    );

    return (this.runSheetDetailData?.safety_prompts || [])?.filter(
      (item: any) => !completedPrompts.has(item?.id),
    );
  }

  async onClockInOutUser() {
    const safetyPrompts = this.requireSafetyCheck();

    if (safetyPrompts?.length) {
      const response = await this.openPrompterComponent(safetyPrompts[0]);

      if (!response) return;
    }
    const dialogRef = this.dialogService.open(
      ClockInOutComponent,
      {
        data: {
          shiftClockInOutParms: this.shiftClockInOutParms,
        },
      },
      this.viewContainerRef,
    );
    const dRes = await firstValueFrom(dialogRef.afterClosed());
    if (!dRes) return;
    this.shiftClockInOutParms.hasUserClockedIn =
      !!dRes?.user_current_clock_status_for_shift?.[0];

    this.shiftClockInOutParms.hasUserClockedOut =
      !!dRes?.user_current_clock_status_for_shift?.[1];
    this.shiftClockInOutParms = {
      ...this.shiftClockInOutParms,
    };
    this.showTasksMenu =
      this.isAdmin ||
      (this.shiftClockInOutParms?.hasUserClockedIn &&
        !this.shiftClockInOutParms?.hasUserClockedOut) ||
      !this.shiftClockInOutParms?.requiresClockInOut;

    this.getRunSheetDetailFromServer();
  }

  enableReporting(checkListData: any) {
    const oldScheduledPatrol =
      new Date(this.runSheetStartDateTime).getTime() < new Date().getTime();

    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: 'Upgrade Task List',
        message: `Upgrade and enable features: ${oldScheduledPatrol ? '' : '\n\t• Capture Attendance'}\n\t• Reporting (Dockets)\n\t• Invoicing\n\t\nDo you want to upgrade the task list?`,
        btn2Detail: { text: 'Upgrade' },
      },
    });
    dialogRef.afterClosed().subscribe((value) => {
      if (value === true) {
        this.updateRunSheet('enable_reporting', checkListData);
      }
    });
  }
  onAssigneeSearch(event: any) {
    if (event.target.value?.length > 0) {
      this.getAssignees({ search_str: event?.target?.value });
    } else {
      this.getAssignees();
    }
  }

  getAssignees(params?: any) {
    const reqParams = params || {};

    this.jobService.getUserList(reqParams).subscribe(async (response: any) => {
      if (response['status'] == 'success') {
        setTimeout(() => {
          this.assigneeList = JSON.parse(JSON.stringify(response?.data));
        }, 50);
      }
    });
  }
  searchPrompts(event: any) {
    if (event.target.value?.length <= 2) return;
    const params: any = { search_str: event.target.value };
    this.getUserPrompts(params);
  }
  getUserPrompts(params?: any, initialCall = false) {
    this.userPromptService
      .getUserPromptList(params)
      .subscribe((response: any) => {
        const resultData = response?.data || [];

        if (initialCall) {
          this.userPromptTotalCount = response?.total_size;
          this.userPromptsSearchResultsCopy = JSON.parse(
            JSON.stringify(resultData?.splice(0, 3)),
          );
        }
        this.userPromptsSearchResults = resultData?.filter(
          (searchPrompt: any) =>
            !this.userPromptsSearchResultsCopy?.some(
              (prompt: any) => prompt?.id == searchPrompt?.id,
            ),
        );
      });
  }
  addPromptTask(promptData: any, checkList: any) {
    checkList.tasks = [
      ...checkList?.tasks?.filter((task: any) => !!task?.note),
      { note: promptData?.name, user_prompt_id: promptData?.id },
      {},
    ];
    setTimeout(() => {
      const inputField: any = document.getElementById('promptSelectId');
      if (inputField) inputField.value = '';
    }, 50);
  }
  onOpenIndex(index: number) {
    this.openIndex = index;
    this.showPatrolDetails();
  }
  showPatrolDetails() {
    const checkLists =
      (this.activeTab == 'pending'
        ? this.runSheetDetailData?.check_list_data?.pending_tasks
        : this.runSheetDetailData?.check_list_data?.completed_tasks) || [];

    if (!checkLists?.length) return;
    if (
      this.openIndex == null ||
      this.openIndex < 0 ||
      this.openIndex >= checkLists.length
    ) {
      return;
    }

    const openedCheckList = checkLists[this.openIndex];
    if (!openedCheckList?.job_data?.id) return;

    if (
      'selectedCheckpoints' in openedCheckList ||
      'cp_exists' in openedCheckList
    )
      return;

    if (openedCheckList?.site_data?.id) {
      this.runSheetService
        .getRunsheetAddInfo(
          {
            job_id: openedCheckList?.job_data?.id,
            site_id: openedCheckList?.site_data?.id,
            runsheet_id: this.runSheetId,
          },
          {},
        )
        .then((response: any) => {
          if (response?.status === 'success') {
            openedCheckList.selectedCheckpoints =
              response?.data?.checkpoints_data_list || [];
            openedCheckList.cp_exists =
              (response?.data?.checkpoints_data_list || [])?.length ||
              response?.data?.cp_exists;
          }
        });
    }
  }
  async onAddCheckpointsToRoute(checkList: any) {
    const dialogRef = this.dialogService.open(AddCheckpointRouteComponent, {
      data: {
        showUpdateFutureShifts: this.runSheetDetailData?.future_schedule_exists
          ? checkList?.check_list_schedule_uuid
          : false,
        selectedCheckpoints: JSON.parse(
          JSON.stringify(checkList?.selectedCheckpoints || []),
        ),
        cpSiteFilter: checkList?.site_data || {},
      },
    });

    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res?.selectedCheckpoints) {
      const reqBody: any = {
        job_id: checkList?.job_data?.id,
        checkpoints: [
          ...res?.selectedCheckpoints?.map((checkpoint: any) => ({
            id: checkpoint?.id,
          })),
        ],
      };
      this.updateRunSheet('update_checkpoints', reqBody, {
        add_to_schedule: this.runSheetDetailData?.future_schedule_exists
          ? res?.updateFutureShifts
          : 0,
      });

      // this.updateData(reqBody);
    }
  }
  openScheduleTemplate() {
    if (!(this.isAdmin || this.isDispatchUser)) return;
    if (!this.runSheetDetailData?.roster_scheduler?.id) return;

    const dialogRef = this.dialogService.open(ChangeScheduleComponent, {
      data: {
        roster_scheduler: this.runSheetDetailData?.roster_scheduler,
        schedule_event_date: this.runSheetDetailData?.schedule_event_date,
      },
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        this.schedulerForm.patchValue(res);
        this.updateRunSheet('schedule');
      }
    });
  }
}
